import { motion } from "framer-motion";
import { Handshake, Mail, Star } from "lucide-react";
import { Link } from "react-router-dom";

const TIERS = [
    {
        tier: "Main Partner",
        count: 1,
        color: "#FF4500",
        slots: ["Sponsor Principale"],
        span: "lg:col-span-12",
        size: "h-64 md:h-80",
    },
    {
        tier: "Gold",
        count: 3,
        color: "#CCFF00",
        slots: ["Gold 01", "Gold 02", "Gold 03"],
        span: "lg:col-span-4",
        size: "h-52",
    },
    {
        tier: "Silver",
        count: 4,
        color: "#FFFFFF",
        slots: ["Silver 01", "Silver 02", "Silver 03", "Silver 04"],
        span: "lg:col-span-3",
        size: "h-44",
    },
    {
        tier: "Technical Partner",
        count: 4,
        color: "#A3A3A3",
        slots: ["Technical 01", "Technical 02", "Technical 03", "Technical 04"],
        span: "lg:col-span-3",
        size: "h-36",
    },
];

function SponsorCard({ slot, tierColor, size }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.4 }}
            data-testid={`sponsor-card-${slot.toLowerCase().replace(/\s+/g, "-")}`}
            className={`group relative ${size} bg-[#0A0A0A] border border-[#2A2A2A] hover:border-[#FF4500] flex items-center justify-center transition-colors duration-300 overflow-hidden`}
        >
            <div
                className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity"
                style={{
                    backgroundImage: `repeating-linear-gradient(-45deg, ${tierColor} 0 2px, transparent 2px 14px)`,
                }}
            />
            <div className="relative flex flex-col items-center justify-center text-center px-4">
                <Star
                    size={22}
                    className="text-[#2A2A2A] group-hover:text-[#FF4500] transition-colors"
                    strokeWidth={2}
                />
                <span className="mt-4 font-bebas text-4xl md:text-5xl uppercase leading-none text-[#1A1A1A] group-hover:text-white transition-colors">
                    {slot}
                </span>
                <span className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] font-bold">
                    Logo disponibile
                </span>
            </div>
            <span
                className="absolute top-0 left-0 h-[3px] w-16"
                style={{ backgroundColor: tierColor }}
            />
        </motion.div>
    );
}

export default function Sponsors() {
    return (
        <div data-testid="page-sponsors" className="text-white">
            {/* Hero */}
            <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-xs uppercase tracking-[0.3em] text-[#FF4500] font-bold"
                >
                    / Partner & Sponsor
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mt-4 font-bebas uppercase leading-[0.85] text-6xl sm:text-7xl md:text-[9vw]"
                >
                    I nostri <br />
                    <span className="text-[#FF4500]">sponsor</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="mt-6 max-w-2xl text-[#A3A3A3] leading-relaxed"
                >
                    Senza di loro il campetto sarebbe spento. Grazie ai brand che
                    credono nel movimento streetball, nel talento giovane e in una
                    comunità che non smette mai di crescere.
                </motion.p>
            </section>

            {/* Tiers */}
            {TIERS.map((tier, tIdx) => (
                <section
                    key={tier.tier}
                    data-testid={`sponsor-tier-${tier.tier.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"
                >
                    <div className="flex items-end justify-between mb-8 border-b border-[#2A2A2A] pb-4">
                        <div className="flex items-baseline gap-4">
                            <span
                                className="w-3 h-3"
                                style={{ backgroundColor: tier.color }}
                            />
                            <h2 className="font-bebas uppercase text-4xl md:text-5xl leading-none">
                                {tier.tier}
                            </h2>
                        </div>
                        <span className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3] font-bold">
                            / {String(tIdx + 1).padStart(2, "0")}
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                        {tier.slots.map((slot) => (
                            <div key={slot} className={tier.span}>
                                <SponsorCard
                                    slot={slot}
                                    tierColor={tier.color}
                                    size={tier.size}
                                />
                            </div>
                        ))}
                    </div>
                </section>
            ))}

            {/* Become sponsor CTA */}
            <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                <div className="relative border border-[#FF4500] p-10 md:p-16 bg-gradient-to-br from-[#0A0A0A] via-[#050505] to-[#0A0A0A] overflow-hidden">
                    <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-[#FF4500]/20 blur-[120px]" />
                    <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        <div>
                            <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] font-bold">
                                / Diventa partner
                            </p>
                            <h2 className="mt-4 font-bebas uppercase text-5xl md:text-6xl leading-[0.9]">
                                Metti il tuo <br />
                                <span className="text-[#FF4500]">brand</span> in evidenza!
                            </h2>
                            <p className="mt-6 text-[#A3A3A3] max-w-md leading-relaxed">
                                Visibilità diretta su agli spettatori,
                                contenuti social, presenza fisica sul parco.
                                Costruiamo insieme il pacchetto giusto.
                            </p>
                        </div>
                        <div className="flex flex-col gap-4 md:items-end">
                            <a
                                href="mailto:infobtr3x3@gmail.com?subject=Sponsorship%20Break%20the%20Rules%203x3"
                                data-testid="sponsor-cta-email"
                                className="inline-flex items-center justify-center gap-3 bg-[#FF4500] text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-[#FF4500] hover:bg-transparent hover:text-[#FF4500] transition-colors duration-300"
                            >
                                <Mail size={18} />
                                Contattaci ora
                            </a>
                            <Link
                                to="/"
                                data-testid="sponsor-cta-home"
                                className="inline-flex items-center justify-center gap-3 bg-transparent text-white uppercase font-extrabold tracking-[0.15em] px-8 py-4 border-2 border-white hover:bg-white hover:text-black transition-colors duration-300"
                            >
                                <Handshake size={18} />
                                Scopri il torneo
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
